import * as Sentry from "@sentry/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import ErrorBoundary from "@/components/error-boundary.tsx";
import { registerDevSharedUsersRequestDebug } from "@/hera-api/register-dev-shared-users-request-debug";
import { registerChunkLoadErrorHandler } from "@/lib/chunk-load-error";
import { sessionStorage } from "@/types/session-storage";
import * as TanStackQueryProvider from "./integrations/tanstack-query/root-provider.tsx";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

import reportWebVitals from "./reportWebVitals.ts";
import "./styles.css";

// Run migration to clear old session storage values
sessionStorage.migrateOldFilters();

// Recover from chunk load failures (e.g. deployment race: user navigates
// to a lazy route whose chunk was replaced by a new deploy)
registerChunkLoadErrorHandler();

registerDevSharedUsersRequestDebug();

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProvider.getContext(),
  },
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
    tracesSampleRate: 0.0, // disable performance tracing, we're not gonna buy it
    sendDefaultPii: true,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  });
}

// Render the app
const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <TanStackQueryProvider.Provider>
          <RouterProvider router={router} notFoundMode="root" />
        </TanStackQueryProvider.Provider>
      </ErrorBoundary>
    </StrictMode>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
